import React from 'react';
import { AlertTriangle, Mail, Phone, MessageCircle, MapPin, Send } from 'lucide-react';

const Footer = () => {
  return (
    <footer id="contact" className="bg-slate-950">
      {/* Risk Warning */}
      <div className="bg-gradient-to-r from-amber-900/20 via-orange-900/20 to-amber-900/20 border-y border-amber-500/30 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 max-w-5xl mx-auto">
            <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-amber-400 font-bold text-lg mb-2">Risk Warning</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Trading in forex, gold, cryptocurrencies, and other financial instruments involves substantial risk and may not be suitable for all investors. 
                The high degree of leverage can work against you as well as for you. Before deciding to trade, you should carefully consider your investment 
                objectives, level of experience, and risk appetite. Past performance is not indicative of future results. There is a possibility that you may 
                sustain a loss equal to or greater than your entire investment. Therefore, you should not invest money that you cannot afford to lose. You should 
                be aware of all the risks associated with automated trading and seek advice from an independent financial advisor if you have any doubts.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-slate-800/50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/logo.png" 
                  alt="Marketwise Solutions Logo" 
                  className="h-12 w-12 object-contain"
                />
                <div className="text-white font-bold text-lg">
                  Marketwise Solutions
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                AI-powered automated trading solutions for modern traders.
              </p>
              <p className="text-slate-500 text-xs">
                Smarter Trading Starts with Marketwise AI
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#products" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Products</a></li>
                <li><a href="#pricing" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Pricing</a></li>
                <li><a href="#performance" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Performance</a></li>
                <li><a href="#about" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">About Us</a></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#faq" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">FAQ</a></li>
                <li><a href="#documentation" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Documentation</a></li>
                <li><a href="#blog" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Blog</a></li>
                <li><a href="#support" className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">Support Center</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:marketwisesolution@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">
                    <Mail className="w-4 h-4" />
                    <span>marketwisesolution@gmail.com</span>
                  </a>
                </li>
                <li>
                  <a href="tel:+971554276609" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">
                    <Phone className="w-4 h-4" />
                    <span>+971 55 427 6609</span>
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/971554276609" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-200">
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp Support</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-2 text-slate-400 text-sm">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>DIP- Office 262 European Business Park - Dubai Investment Park First - Dubai</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-800/50">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-500 text-sm">
                © 2024 Marketwise Solutions. All rights reserved.
              </p>
              <div className="flex gap-6">
                <a href="#privacy" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-200">Privacy Policy</a>
                <a href="#terms" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-200">Terms of Service</a>
                <a href="#disclaimer" className="text-slate-500 hover:text-cyan-400 text-sm transition-colors duration-200">Disclaimer</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Widget */}
      <a 
        href="https://wa.me/971554276609?text=Hello%20Marketwise%20Solutions%2C%20I%27m%20interested%20in%20your%20AI%20trading%20platform"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-full flex items-center justify-center shadow-2xl shadow-cyan-500/50 hover:scale-110 transition-all duration-300 z-50"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </a>
    </footer>
  );
};

export default Footer;
